import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Copy, Settings, Eye, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { copyToClipboard } from "@/lib/utils";
import { Database } from "@/types/database.types";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

type Tables<T extends keyof Database["public"]["Tables"]> =
  Database["public"]["Tables"][T]["Row"];

interface AgentChatBubbleTabProps {
  agent: Tables<"agents">;
  updateAgent: (data: Partial<any>) => Promise<boolean>;
}

// Simple block component that matches agent details styling
const DetailBlock: React.FC<{
  icon: React.ReactNode;
  title: string;
  iconBgColor?: string;
  iconTextColor?: string;
  children: React.ReactNode;
}> = ({
  icon,
  title,
  iconBgColor = "bg-amber-100",
  iconTextColor = "text-amber-500",
  children,
}) => {
  return (
    <Card className="border rounded-lg">
      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <div className={`p-2 ${iconBgColor} rounded-md ${iconTextColor}`}>
              {icon}
            </div>
            <h3 className="text-lg font-medium">{title}</h3>
          </div>
          {children}
        </div>
      </CardContent>
    </Card>
  );
};

const AgentChatBubbleTab: React.FC<AgentChatBubbleTabProps> = ({
  agent,
  updateAgent,
}) => {
  const [welcomeMessage, setWelcomeMessage] = useState(
    agent.welcome_message || "Hi! How can I assist you?",
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [testUrl, setTestUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [gtmCopied, setGtmCopied] = useState(false);
  const { toast } = useToast();
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setOrigin(window.location.origin);
    }
  }, []);

  useEffect(() => {
    // Generate a test link for the chat bubble
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : "";
    const protocol = window.location.protocol;

    setTestUrl(
      `${protocol}//${hostname}${port}/preview-bubble/${agent.public_id}`,
    );
  }, [agent.public_id]);

  const handleUpdateWelcomeMessage = async () => {
    setIsUpdating(true);
    try {
      const success = await updateAgent({ welcome_message: welcomeMessage });
      if (success) {
        toast({
          title: "Welcome message updated",
          description: "Your chat bubble welcome message has been updated.",
        });
      } else {
        throw new Error("Failed to update welcome message");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description:
          "There was a problem updating your welcome message. Please try again.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const generateEmbedCode = () => {
    const scriptCode = `<script 
  src="${origin}/embed.js" 
  data-agent-id="${agent.public_id}" 
  data-type="bubble" 
  data-welcome-message="${welcomeMessage.replace(/'/g, "\\'")}"
></script>`;

    return scriptCode;
  };

  const generateGtmCode = () => {
    const gtmCode = `<script>
// Set configuration for GTM compatibility
window.hubfloWidgetConfig = {
  agentId: ${JSON.stringify(agent.public_id)},
  type: 'bubble',
  welcomeMessage: ${JSON.stringify(welcomeMessage)}
};

// Load the widget script
var script = document.createElement('script');
script.src = ${JSON.stringify(origin + "/embed.js")};

// Safely append script with fallback
if (document.body) {
  document.body.appendChild(script);
} else if (document.head) {
  document.head.appendChild(script);
} else {
  document.documentElement.appendChild(script);
}
</script>`;

    return gtmCode;
  };

  const handleCopyEmbedCode = () => {
    copyToClipboard(generateEmbedCode(), "Embed code copied to clipboard!");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyGtmCode = () => {
    copyToClipboard(generateGtmCode(), "GTM code copied to clipboard!");
    setGtmCopied(true);
    setTimeout(() => setGtmCopied(false), 2000);
  };

  return (
    <div className="h-full bg-white overflow-hidden">
      {/* Welcome Message Configuration */}
      <DetailBlock
        icon={<Settings className="h-5 w-5" />}
        title="Customize Chat Bubble"
        iconBgColor="bg-blue-100"
        iconTextColor="text-blue-500"
      >
        <div className="space-y-2">
          <Label
            htmlFor="welcomeMessage"
            className="text-sm font-medium text-gray-700"
          >
            Welcome Message
          </Label>
          <div className="flex gap-2">
            <Input
              id="welcomeMessage"
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              placeholder="Hi! How can I assist you?"
              className="flex-1"
            />
            <Button onClick={handleUpdateWelcomeMessage} disabled={isUpdating}>
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            This message will appear when users first open the chat bubble.
          </p>
        </div>
      </DetailBlock>

      {/* Embed Code Section */}
      <div className="flex-1 p-6 overflow-y-auto overflow-x-hidden">
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Chat Bubble Code
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            Copy and paste this code into your website to add the chat bubble.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-blue-800 text-xs">
              <strong>Note:</strong> This code works for direct injection into
              HTML. If you're using Google Tag Manager, scroll down to the GTM
              section for the special GTM-compatible code.
            </p>
          </div>

          <div className="relative">
            <div className="bg-gray-50 border rounded-lg p-4 text-sm font-mono max-h-40 overflow-hidden">
              <div className="break-all whitespace-pre-wrap text-wrap overflow-hidden">
                {generateEmbedCode()}
              </div>
            </div>
            <div className="absolute top-2 right-2 flex gap-2">
              <Button
                onClick={() =>
                  window.open(testUrl, "_blank", "noopener,noreferrer")
                }
                className="h-8 w-8 p-0"
                variant="outline"
                size="sm"
                title="Preview embed"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                onClick={handleCopyEmbedCode}
                className="h-8 w-8 p-0"
                variant="outline"
                size="sm"
                title="Copy code"
              >
                {copied ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
            <Eye className="h-4 w-4" />
            <span>
              Use the preview button to test your chat bubble before adding it
              to your website.
            </span>
          </div>
        </div>

        {/* Platform-Specific Instructions */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Platform Instructions
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Choose your platform below for step-by-step installation
            instructions.
          </p>

          <Accordion type="single" collapsible className="w-full space-y-2">
            {/* WordPress */}
            <AccordionItem value="wordpress" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">W</span>
                  </div>
                  <span className="font-medium">WordPress</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold mb-2">
                      Method 1: Using a Plugin (Recommended)
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Install the "Insert Headers and Footers" plugin</li>
                      <li>Go to Settings → Insert Headers and Footers</li>
                      <li>Paste the code in the "Scripts in Footer" section</li>
                      <li>Click "Save"</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">
                      Method 2: Theme Files
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Go to Appearance → Theme Editor</li>
                      <li>Select "Theme Footer (footer.php)"</li>
                      <li>
                        Paste the code before the closing &lt;/body&gt; tag
                      </li>
                      <li>Click "Update File"</li>
                    </ol>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-amber-800 text-xs">
                      <strong>Note:</strong> Method 1 is recommended as theme
                      updates won't remove your code.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Squarespace */}
            <AccordionItem value="squarespace" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-black rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">S</span>
                  </div>
                  <span className="font-medium">Squarespace</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold mb-2">
                      Method 1: Code Injection (Site-wide)
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Go to Settings → Advanced → Code Injection</li>
                      <li>Paste the code in the "Footer" section</li>
                      <li>Click "Save"</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">
                      Method 2: Code Block (Specific Pages)
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Edit the page where you want the chat</li>
                      <li>Click the "+" icon to add a block</li>
                      <li>Select "Code" from the block menu</li>
                      <li>Paste the code and click "Apply"</li>
                    </ol>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-blue-800 text-xs">
                      <strong>Tip:</strong> Use Method 1 to show the chat on all
                      pages, or Method 2 for specific pages only.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Wix */}
            <AccordionItem value="wix" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-purple-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">W</span>
                  </div>
                  <span className="font-medium">Wix</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold mb-2">Using Wix Editor</h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Go to Settings → Custom Code in your Wix Editor</li>
                      <li>Click "+ Add Custom Code"</li>
                      <li>Paste the code in the code box</li>
                      <li>Choose "Body - end" for code placement</li>
                      <li>Select "All pages" or specific pages</li>
                      <li>Click "Apply"</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">
                      Alternative: HTML Embed
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Click "Add" → "Embed" → "HTML iframe"</li>
                      <li>Click "Enter Code"</li>
                      <li>Paste the code and click "Update"</li>
                      <li>
                        Position the invisible iframe anywhere on your page
                      </li>
                    </ol>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <p className="text-purple-800 text-xs">
                      <strong>Note:</strong> The first method is recommended for
                      site-wide chat functionality.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Google Tag Manager */}
            <AccordionItem value="gtm" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">G</span>
                  </div>
                  <span className="font-medium">Google Tag Manager</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4 text-sm">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-amber-800 text-xs">
                      <strong>Important:</strong> For GTM, use the special code
                      below instead of the basic embed code above.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">GTM-Compatible Code</h4>
                    <p className="text-gray-600 mb-2">
                      Copy this code for Google Tag Manager:
                    </p>
                    <div className="relative">
                      <div className="bg-gray-900 text-green-400 rounded-lg p-3 font-mono text-xs overflow-hidden">
                        <div className="whitespace-pre-wrap break-all text-wrap overflow-hidden">
                          {generateGtmCode()}
                        </div>
                      </div>
                      <div className="absolute top-2 right-2">
                        <Button
                          onClick={handleCopyGtmCode}
                          className="h-8 w-8 p-0"
                          variant="outline"
                          size="sm"
                          title="Copy GTM code"
                        >
                          {gtmCopied ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Setup Instructions</h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Log in to your Google Tag Manager account</li>
                      <li>Select your website container</li>
                      <li>Click "Tags" → "New"</li>
                      <li>Choose "Custom HTML" as tag type</li>
                      <li>
                        Paste the GTM-compatible code above in the HTML field
                      </li>
                      <li>
                        Set trigger to "All Pages" (or create a custom trigger)
                      </li>
                      <li>Save and publish the container</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">
                      Advanced: Page-Specific Loading
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Create a new trigger</li>
                      <li>Choose "Page View" trigger type</li>
                      <li>
                        Set conditions (e.g., Page URL contains "contact")
                      </li>
                      <li>Apply this trigger to your chat tag</li>
                    </ol>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-green-800 text-xs">
                      <strong>Why different code?</strong> GTM loads scripts
                      differently than direct injection. This special code
                      ensures the widget works properly in GTM environments.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Custom HTML/Website */}
            <AccordionItem value="custom" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-gray-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      &lt;/&gt;
                    </span>
                  </div>
                  <span className="font-medium">Custom HTML Website</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold mb-2">
                      Direct HTML Implementation
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Open your HTML file(s) in a text editor</li>
                      <li>Locate the closing &lt;/body&gt; tag</li>
                      <li>Paste the code just before the &lt;/body&gt; tag</li>
                      <li>Save the file(s)</li>
                      <li>Upload to your web server</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">
                      Template/Include Method
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Create a new file (e.g., chat-widget.html)</li>
                      <li>Add the code to this file</li>
                      <li>Include it in your template footer</li>
                      <li>
                        Example: &lt;?php include 'chat-widget.html'; ?&gt;
                      </li>
                    </ol>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-gray-800 text-xs">
                      <strong>Best Practice:</strong> Place the code just before
                      &lt;/body&gt; for optimal page load performance.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Shopify */}
            <AccordionItem value="shopify" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">S</span>
                  </div>
                  <span className="font-medium">Shopify</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold mb-2">Theme File Method</h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Go to Online Store → Themes</li>
                      <li>Click "Actions" → "Edit code"</li>
                      <li>Open "theme.liquid" file</li>
                      <li>
                        Paste the code before the closing &lt;/body&gt; tag
                      </li>
                      <li>Click "Save"</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">
                      App/Script Tag Method
                    </h4>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>Go to Settings → Checkout</li>
                      <li>Scroll to "Order status page" scripts</li>
                      <li>Paste code for post-purchase chat</li>
                      <li>Or use Settings → Web Pixels for site-wide</li>
                    </ol>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-green-800 text-xs">
                      <strong>Tip:</strong> Consider customer support during
                      checkout vs. browsing phases.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* General Tips Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">💡 General Tips</h4>
          <ul className="space-y-1 text-sm text-blue-800">
            <li>• Test the chat widget on both desktop and mobile devices</li>
            <li>
              • Clear your browser cache if changes don't appear immediately
            </li>
            <li>
              • The chat bubble will appear in the bottom-right corner by
              default
            </li>
            <li>
              • Contact your web developer if you're unsure about code placement
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AgentChatBubbleTab;
